const hoverFocusAndOtherStates = {
  name: "Hover, Focus, and Other States",
  path: "hover-focus-and-other-states",
  title: "Hover, Focus, and Other States",
  description: "Using utilities to style elements on hover, focus, and more.",
  heading: false,
  exampleData: [
    {
      title: ":hover",
      description: "Style elements when a user hovers over them",
      moreDescription: "Use the hover variant to style elements on hover:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<button class="bg-color-blue-500 hover:bg-color-blue-600 text-color-white px-4 py-2 rounded">
  Hover me
</button>`
      },
    },
    {
      title: ":focus",
      description: "Style elements when they receive focus",
      moreDescription: "Use the focus variant to style elements when they have focus:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<input class="border-2 border-color-gray-300 focus:border-color-blue-500 focus:outline-none px-3 py-2 rounded" placeholder="Click to focus">`
      },
    },
    {
      title: ":active",
      description: "Style elements when they are being pressed",
      moreDescription: "Use the active variant to style elements when they are being activated:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<button class="bg-color-green-500 active:bg-color-green-700 text-color-white px-4 py-2 rounded">
  Press me
</button>`
      },
    },
    {
      title: ":visited",
      description: "Style links that have been visited",
      moreDescription: "Use the visited variant to style visited links:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<a href="#" class="text-color-blue-600 visited:text-color-purple-600 underline">
  Visit this link
</a>`
      },
    },
    {
      title: ":disabled",
      description: "Style elements when they are disabled",
      moreDescription: "Use the disabled variant to style disabled form elements:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<button disabled class="bg-color-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-color-gray-500 px-4 py-2 rounded">
  Disabled button
</button>`
      },
    },
    {
      title: ":checked",
      description: "Style checkboxes and radio buttons when checked",
      moreDescription: "Use the checked variant to style checked inputs:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<input type="checkbox" class="checked:bg-color-blue-500 checked:border-color-blue-500" checked>`
      },
    },
    {
      title: ":focus-visible",
      description: "Style elements when they have visible focus",
      moreDescription: "Use the focus-visible variant for keyboard navigation focus:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<button class="bg-color-blue-500 focus-visible:ring-2 focus-visible:ring-color-blue-300 text-color-white px-4 py-2 rounded">
  Tab to focus
</button>`
      },
    },
    {
      title: ":invalid",
      description: "Style form elements when their value is invalid",
      moreDescription: "Use the invalid variant to style invalid form inputs:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<input type="email" required class="border-2 border-color-gray-300 invalid:border-color-red-500 px-3 py-2 rounded" placeholder="Enter invalid email">`
      },
    },
    {
      title: ":required",
      description: "Style required form elements",
      moreDescription: "Use the required variant to style required inputs:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<input type="text" required class="border-2 border-color-gray-300 required:border-color-orange-400 px-3 py-2 rounded" placeholder="Required field">`
      },
    },
    {
      title: ":read-only",
      description: "Style read-only form elements",
      moreDescription: "Use the read-only variant to style readonly inputs:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<input type="text" readonly value="Read-only text" class="border-2 border-color-gray-300 read-only:bg-color-gray-100 px-3 py-2 rounded">`
      },
    },
    {
      title: ":valid",
      description: "Style form elements when their value is valid",
      moreDescription: "Use the valid variant to style valid form inputs:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<input type="email" class="border-2 border-color-gray-300 valid:border-color-green-500 px-3 py-2 rounded" placeholder="user@example.com">`
      },
    },
    {
      title: ":in-range",
      description: "Style inputs when their value is within range",
      moreDescription: "Use the in-range variant for range inputs:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<input type="number" min="1" max="10" value="5" class="border-2 border-color-gray-300 in-range:border-color-green-500 px-3 py-2 rounded">`
      },
    },
    {
      title: ":out-of-range",
      description: "Style inputs when their value is out of range",
      moreDescription: "Use the out-of-range variant for invalid range values:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<input type="number" min="1" max="10" value="15" class="border-2 border-color-gray-300 out-of-range:border-color-red-500 px-3 py-2 rounded">`
      },
    },
    {
      title: ":focus-within",
      description: "Style elements when they contain a focused element",
      moreDescription: "Use the focus-within variant to style parent elements:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<div class="border-2 border-color-gray-300 focus-within:border-color-blue-500 p-4 rounded">
  <input class="outline-none" placeholder="Focus me">
</div>`
      },
    },
    {
      title: ":target",
      description: "Style elements that are the target of a URL fragment",
      moreDescription: "Use the target variant for URL hash targets:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<div id="section1" class="target:bg-color-yellow-200 p-4 border rounded">
  Target section (add #section1 to URL)
</div>`
      },
    },
    {
      title: "::before",
      description: "Style pseudo-elements before content",
      moreDescription: "Use the before variant to add content before elements:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<div class="before:content-['→'] before:mr-2 before:text-color-blue-500">
  Before pseudo-element
</div>`
      },
    },
    {
      title: "::after",
      description: "Style pseudo-elements after content",
      moreDescription: "Use the after variant to add content after elements:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<div class="after:content-['←'] after:ml-2 after:text-color-red-500">
  After pseudo-element
</div>`
      },
    },
    {
      title: ":first-child",
      description: "Style the first child element",
      moreDescription: "Use the first variant to style the first child element:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<div class="space-y-2">
  <div class="first:bg-color-blue-500 first:text-color-white p-2 rounded">First item</div>
  <div class="first:bg-color-blue-500 first:text-color-white p-2 rounded">Second item</div>
  <div class="first:bg-color-blue-500 first:text-color-white p-2 rounded">Third item</div>
</div>`
      },
    },
    {
      title: ":last-child",
      description: "Style the last child element",
      moreDescription: "Use the last variant to style the last child element:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<div class="space-y-2">
  <div class="last:bg-color-red-500 last:text-color-white p-2 rounded">First item</div>
  <div class="last:bg-color-red-500 last:text-color-white p-2 rounded">Second item</div>
  <div class="last:bg-color-red-500 last:text-color-white p-2 rounded">Last item</div>
</div>`
      },
    },
    {
      title: ":not(:first-child)",
      description: "Style all elements except the first child",
      moreDescription: "Use the not-first variant to style all children except the first:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<div class="space-y-2">
  <div class="not-first:border-t not-first:border-color-gray-300 p-2">First item</div>
  <div class="not-first:border-t not-first:border-color-gray-300 p-2">Second item</div>
  <div class="not-first:border-t not-first:border-color-gray-300 p-2">Third item</div>
</div>`
      },
    },
    {
      title: ":not(:last-child)",
      description: "Style all elements except the last child",
      moreDescription: "Use the not-last variant to style all children except the last:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<div class="space-y-2">
  <div class="not-last:border-b not-last:border-color-gray-300 p-2">First item</div>
  <div class="not-last:border-b not-last:border-color-gray-300 p-2">Second item</div>
  <div class="not-last:border-b not-last:border-color-gray-300 p-2">Last item</div>
</div>`
      },
    },
    {
      title: ":nth-child(odd)",
      description: "Style odd-numbered child elements",
      moreDescription: "Use the odd variant to style odd-numbered children:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<div class="space-y-2">
  <div class="odd:bg-color-gray-100 p-2 rounded">Item 1</div>
  <div class="odd:bg-color-gray-100 p-2 rounded">Item 2</div>
  <div class="odd:bg-color-gray-100 p-2 rounded">Item 3</div>
  <div class="odd:bg-color-gray-100 p-2 rounded">Item 4</div>
</div>`
      },
    },
    {
      title: ":nth-child(even)",
      description: "Style even-numbered child elements",
      moreDescription: "Use the even variant to style even-numbered children:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<div class="space-y-2">
  <div class="even:bg-color-blue-50 p-2 rounded">Item 1</div>
  <div class="even:bg-color-blue-50 p-2 rounded">Item 2</div>
  <div class="even:bg-color-blue-50 p-2 rounded">Item 3</div>
  <div class="even:bg-color-blue-50 p-2 rounded">Item 4</div>
</div>`
      },
    },
    {
      title: "::placeholder",
      description: "Style input placeholder text",
      moreDescription: "Use the placeholder variant to style placeholder text:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<input class="placeholder:text-color-gray-400 placeholder:italic border-2 border-color-gray-300 px-3 py-2 rounded" placeholder="Custom placeholder style">`
      },
    },
    {
      title: "::file-selector-button",
      description: "Style file input buttons",
      moreDescription: "Use the file variant to style file selector buttons:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<input type="file" class="file:bg-color-blue-500 file:text-color-white file:border-0 file:px-4 file:py-2 file:rounded file:mr-4">`
      },
    },
    {
      title: "::marker",
      description: "Style list item markers",
      moreDescription: "Use the marker variant to style list markers:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<ul class="list-disc pl-6 space-y-2">
  <li class="marker:text-color-blue-500">Blue marker item</li>
  <li class="marker:text-color-red-500">Red marker item</li>
  <li class="marker:text-color-green-500">Green marker item</li>
</ul>`
      },
    },
    {
      title: "::selection",
      description: "Style selected text",
      moreDescription: "Use the selection variant to style text selection:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<p class="selection:bg-color-yellow-200 selection:text-color-black">
  Select this text to see the custom selection styling
</p>`
      },
    },
    {
      title: "::first-line",
      description: "Style the first line of text",
      moreDescription: "Use the first-line variant to style the first line:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<p class="first-line:font-bold first-line:text-color-blue-600">
  This is the first line of text that will be styled differently.
  This is the second line that will have normal styling.
</p>`
      },
    },
    {
      title: "::first-letter",
      description: "Style the first letter of text",
      moreDescription: "Use the first-letter variant to style the first letter:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<p class="first-letter:text-4xl first-letter:font-bold first-letter:text-color-red-500 first-letter:float-left first-letter:mr-2">
  This paragraph has a styled first letter that creates a drop cap effect.
</p>`
      },
    },
    {
      title: "::backdrop",
      description: "Style modal/dialog backdrops",
      moreDescription: "Use the backdrop variant to style dialog backdrops:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<dialog class="backdrop:bg-color-black backdrop:opacity-50">
  <div class="p-6 bg-color-white rounded">
    Modal content with custom backdrop
  </div>
</dialog>`
      },
    },
    {
      title: ":has(:checked)",
      description: "Style elements that contain checked inputs",
      moreDescription: "Use the has-checked variant to style parent elements:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<label class="has-checked:bg-color-green-100 has-checked:border-color-green-500 border-2 border-color-gray-300 p-4 rounded flex items-center space-x-2">
  <input type="checkbox" class="w-4 h-4">
  <span>Check me to change parent styling</span>
</label>`
      },
    },
    // {
    //   title: ":indeterminate",
    //   description: "Style indeterminate checkboxes",
    //   moreDescription: "Use the indeterminate variant for partially checked states:",
    //   parentClass: "!bg-none !border-0",
    //   example: {
    //     element: "div",
    //     className: "relative",
    //     code: `<input type="checkbox" class="indeterminate:bg-color-blue-500 indeterminate:border-color-blue-500 w-4 h-4" indeterminate>`
    //   },
    // },
    {
      title: "peer-*",
      description: "Style elements based on sibling states",
      moreDescription: "Use peer variants to style based on sibling element states:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<div class="space-y-2">
  <input class="peer border-2 border-color-gray-300 px-3 py-2 rounded" placeholder="Focus me">
  <p class="peer-focus:text-color-blue-600 peer-focus:font-semibold">This text changes when input is focused</p>
</div>`
      },
    },
    {
      title: "not-*",
      description: "Style elements when NOT in certain states",
      moreDescription: "Use not variants to style elements when they're not in specific states:",
      parentClass: "!bg-none !border-0",
      example: {
        element: "div",
        className: "relative",
        code: `<button class="not-hover:opacity-75 not-disabled:cursor-pointer bg-color-blue-500 text-color-white px-4 py-2 rounded">
  Not hover styling
</button>`
      },
    }
  ],
  htmlData: [
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">hover:</code>
              utilities to style elements on hover:
        </p>

        <div class="space-y-4">
            <button class="bg-color-blue-500 hover:bg-color-blue-600 text-color-white px-4 py-2 rounded transition-colors">
              Hover me
            </button>
            
            <div class="bg-color-gray-200 hover:bg-color-blue-100 hover:scale-105 hover:shadow-lg transition-all duration-300 p-4 rounded cursor-pointer">
              Hover for transform and shadow
            </div>
            
            <p class="text-color-gray-600 hover:text-color-blue-600 hover:underline cursor-pointer transition-colors">
              Hover to change color and add underline
            </p>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">focus:</code>
              utilities to style elements when they have focus:
        </p>

        <div class="space-y-4">
            <input class="w-full border-2 border-color-gray-300 focus:border-color-blue-500 focus:outline-none px-3 py-2 rounded transition-colors" placeholder="Click to focus">
            
            <input class="w-full border-2 border-color-gray-300 focus:border-color-blue-500 focus:ring-2 focus:ring-color-blue-200 px-3 py-2 rounded transition-all" placeholder="Focus with ring">
            
            <textarea class="w-full border-2 border-color-gray-300 focus:border-color-purple-500 focus:bg-color-purple-50 focus:shadow-lg transition-all px-3 py-2 rounded" placeholder="Focus for multiple effects" rows="3"></textarea>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">active:</code>
              utilities to style elements when they are being pressed:
        </p>

        <div class="flex flex-wrap gap-4">
            <button class="bg-color-green-500 active:bg-color-green-700 text-color-white px-4 py-2 rounded transition-colors">
              Press me
            </button>
            
            <button class="bg-color-red-500 active:bg-color-red-700 active:scale-95 text-color-white px-4 py-2 rounded transition-all">
              Press for scale effect
            </button>
            
            <button class="bg-color-purple-500 active:bg-color-purple-700 active:shadow-inner shadow-lg text-color-white px-4 py-2 rounded transition-all">
              Press for shadow change
            </button>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">visited:</code>
              utilities to style visited links:
        </p>

        <div class="space-y-4">
            <a href="#example1" class="block text-color-blue-600 visited:text-color-purple-600 underline">
              Visit this link (will turn purple after clicking)
            </a>
            
            <a href="#example2" class="block text-color-green-600 visited:text-color-orange-600 hover:text-color-green-700 visited:hover:text-color-orange-700 underline">
              Another link with visited state
            </a>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">disabled:</code>
              utilities to style disabled form elements:
        </p>

        <div class="flex flex-wrap gap-4">
            <button disabled class="bg-color-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-color-gray-500 px-4 py-2 rounded">
              Disabled button
            </button>
            
            <input disabled class="border-2 border-color-gray-300 disabled:bg-color-gray-100 disabled:text-color-gray-400 px-3 py-2 rounded" placeholder="Disabled input">
            
            <select disabled class="border-2 border-color-gray-300 disabled:bg-color-gray-100 disabled:text-color-gray-400 px-3 py-2 rounded">
                <option>Disabled select</option>
            </select>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">checked:</code>
              utilities to style checked inputs:
        </p>

        <div class="space-y-4">
            <label class="flex items-center space-x-2">
                <input type="checkbox" class="w-4 h-4 checked:bg-color-green-500 checked:border-color-green-500" checked>
                <span>Checked checkbox</span>
            </label>
            
            <label class="flex items-center space-x-2">
                <input type="radio" name="example" class="w-4 h-4 checked:bg-color-green-500 checked:border-color-green-500" checked>
                <span>Checked radio button</span>
            </label>
            
            <label class="flex items-center space-x-2">
                <input type="checkbox" class="w-4 h-4 checked:bg-color-purple-500 checked:border-color-purple-500 checked:scale-110 transition-transform">
                <span>Checkbox with scale effect</span>
            </label>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">focus-visible:</code>
              utilities for keyboard navigation focus:
        </p>

        <div class="space-y-4">
            <button class="bg-color-blue-500 focus-visible:ring-2 focus-visible:ring-color-blue-300 text-color-white px-4 py-2 rounded">
              Tab to focus (keyboard only)
            </button>
            
            <button class="bg-color-green-500 focus-visible:ring-4 focus-visible:ring-color-green-200 focus-visible:ring-offset-2 text-color-white px-4 py-2 rounded">
              Focus with offset ring
            </button>
            
            <input class="border-2 border-color-gray-300 focus-visible:border-color-purple-500 focus-visible:ring-2 focus-visible:ring-color-purple-200 px-3 py-2 rounded" placeholder="Tab to focus input">
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">invalid:</code>
              utilities to style invalid form inputs:
        </p>

        <div class="space-y-4">
            <input type="email" required class="w-full border-2 border-color-gray-300 invalid:border-color-red-500 invalid:text-color-red-600 px-3 py-2 rounded" placeholder="Enter invalid email">
            
            <input type="url" class="w-full border-2 border-color-gray-300 invalid:border-color-red-500 invalid:bg-color-red-50 px-3 py-2 rounded" placeholder="Enter invalid URL">
            
            <input type="number" min="1" max="5" class="w-full border-2 border-color-gray-300 invalid:border-color-red-500 invalid:shadow-red-200 invalid:shadow-lg px-3 py-2 rounded" placeholder="Enter number 1-5">
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">required:</code>
              utilities to style required inputs:
        </p>

        <div class="space-y-4">
            <input type="text" required class="w-full border-2 border-color-gray-300 required:border-color-orange-400 px-3 py-2 rounded" placeholder="Required field">
            
            <textarea required class="w-full border-2 border-color-gray-300 required:border-color-orange-400 required:border-l-4 px-3 py-2 rounded" placeholder="Required textarea" rows="3"></textarea>
            
            <select required class="w-full border-2 border-color-gray-300 required:border-color-orange-400 required:bg-color-orange-50 px-3 py-2 rounded">
                <option value="">Select required option</option>
                <option value="1">Option 1</option>
                <option value="2">Option 2</option>
            </select>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">read-only:</code>
              utilities to style readonly inputs:
        </p>

        <div class="space-y-4">
            <input type="text" readonly value="Read-only text" class="w-full border-2 border-color-gray-300 read-only:bg-color-gray-100 px-3 py-2 rounded">
            
            <input type="email" readonly value="readonly@example.com" class="w-full border-2 border-color-gray-300 read-only:bg-color-blue-50 read-only:border-color-blue-300 px-3 py-2 rounded">
            
            <textarea readonly class="w-full border-2 border-color-gray-300 read-only:bg-color-gray-100 read-only:text-color-gray-600 px-3 py-2 rounded" rows="3">This is a read-only textarea. You cannot edit this content.</textarea>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">valid:</code>
              utilities to style valid form inputs:
        </p>

        <div class="space-y-4">
            <input type="email" value="user@example.com" class="w-full border-2 border-color-gray-300 valid:border-color-green-500 px-3 py-2 rounded" placeholder="user@example.com">
            
            <input type="url" value="https://example.com" class="w-full border-2 border-color-gray-300 valid:border-color-green-500 valid:bg-color-green-50 px-3 py-2 rounded" placeholder="https://example.com">
            
            <input type="number" min="1" max="10" value="5" class="w-full border-2 border-color-gray-300 valid:border-color-green-500 valid:shadow-green-200 valid:shadow-md px-3 py-2 rounded" placeholder="Enter number 1-10">
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">in-range:</code>
              utilities for inputs with values in range:
        </p>

        <div class="space-y-4">
            <input type="number" min="1" max="10" value="5" class="w-full border-2 border-color-gray-300 in-range:border-color-green-500 px-3 py-2 rounded" placeholder="1-10">
            
            <input type="range" min="0" max="100" value="50" class="w-full in-range:accent-color-green-500">
            
            <input type="date" min="2024-01-01" max="2024-12-31" value="2024-06-15" class="w-full border-2 border-color-gray-300 in-range:border-color-green-500 in-range:bg-color-green-50 px-3 py-2 rounded">
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">out-of-range:</code>
              utilities for invalid range values:
        </p>

        <div class="space-y-4">
            <input type="number" min="1" max="10" value="15" class="w-full border-2 border-color-gray-300 out-of-range:border-color-red-500 px-3 py-2 rounded" placeholder="1-10 (try 15)">
            
            <input type="range" min="0" max="100" value="150" class="w-full out-of-range:accent-color-red-500">
            
            <input type="date" min="2024-01-01" max="2024-12-31" value="2025-01-01" class="w-full border-2 border-color-gray-300 out-of-range:border-color-red-500 out-of-range:bg-color-red-50 px-3 py-2 rounded">
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">focus-within:</code>
              utilities to style parent elements:
        </p>

        <div class="space-y-4">
            <div class="border-2 border-color-gray-300 focus-within:border-color-blue-500 p-4 rounded">
                <input class="w-full outline-none" placeholder="Focus me to change parent border">
            </div>
            
            <div class="border-2 border-color-gray-300 focus-within:border-color-purple-500 focus-within:bg-color-purple-50 focus-within:shadow-lg p-4 rounded transition-all">
                <label class="block text-sm font-medium mb-2">Form Group</label>
                <input class="w-full border border-color-gray-200 outline-none px-3 py-2 rounded" placeholder="Focus changes entire container">
            </div>
            
            <div class="border-2 border-color-gray-300 focus-within:border-color-green-500 focus-within:scale-105 p-4 rounded transition-transform">
                <textarea class="w-full outline-none resize-none" placeholder="Focus for container transform" rows="3"></textarea>
            </div>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">target:</code>
              utilities for URL hash targets:
        </p>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">before:</code>
              utilities to add content before elements:
        </p>

        <div class="space-y-4">
            <div class="before:content-[→] before:mr-2 before:text-color-blue-500">
              Before pseudo-element with arrow
            </div>

            <div class="before:content-[⭐] before:mr-2 before:text-color-yellow-500 before:text-lg">
              Before with star icon
            </div>
            
            <div class="before:content-[] before:w-4 before:h-4 before:bg-color-red-500 before:rounded-full before:inline-block before:mr-2">
              Before with colored circle
            </div>
            
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">after:</code>
              utilities to add content after elements:
        </p>

        <div class="space-y-4">
            <div class="after:content-[←] after:ml-2 after:text-color-red-500">
              After pseudo-element with arrow
            </div>
            
            <div class="after:content-[✓] after:ml-2 after:text-color-green-500 after:font-bold">
              After with checkmark
            </div>
            
            <div class="after:content-[] after:w-4 after:h-4 after:bg-color-purple-500 after:rounded-full after:inline-block after:ml-2">
              After with colored circle
            </div>
            
            <a href="#" class="text-color-blue-600 after:content-[_↗] after:text-sm after:text-color-blue-400 hover:after:text-color-blue-600">
              External link with after icon
            </a>
            
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">first:</code>
              utilities to style the first child element:
        </p>

        <div class="space-y-4">
            <div class="border border-color-gray-200 rounded p-4">
                <div class="space-y-2">
                    <div class="first:bg-color-blue-500 first:text-color-white bg-color-gray-100 p-3 rounded">First item (styled)</div>
                    <div class="first:bg-color-blue-500 first:text-color-white bg-color-gray-100 p-3 rounded">Second item</div>
                    <div class="first:bg-color-blue-500 first:text-color-white bg-color-gray-100 p-3 rounded">Third item</div>
                </div>
            </div>
            
            <div class="border border-color-gray-200 rounded p-4">
                <ul class="space-y-2">
                    <li class="first:font-bold first:text-color-green-600 p-2 bg-color-gray-50 rounded">First list item (bold & green)</li>
                    <li class="first:font-bold first:text-color-green-600 p-2 bg-color-gray-50 rounded">Second list item</li>
                    <li class="first:font-bold first:text-color-green-600 p-2 bg-color-gray-50 rounded">Third list item</li>
                </ul>
            </div>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">last:</code>
              utilities to style the last child element:
        </p>

        <div class="space-y-4">
            <div class="border border-color-gray-200 rounded p-4">
                <div class="space-y-2">
                    <div class="last:bg-color-red-500 last:text-color-white bg-color-gray-100 p-3 rounded">First item</div>
                    <div class="last:bg-color-red-500 last:text-color-white bg-color-gray-100 p-3 rounded">Second item</div>
                    <div class="last:bg-color-red-500 last:text-color-white bg-color-gray-100 p-3 rounded">Last item (styled)</div>
                </div>
            </div>
            
            <div class="border border-color-gray-200 rounded p-4">
                <nav class="flex space-x-4">
                    <a href="#" class="last:font-bold last:text-color-purple-600 text-color-blue-600 hover:underline">Home</a>
                    <a href="#" class="last:font-bold last:text-color-purple-600 text-color-blue-600 hover:underline">About</a>
                    <a href="#" class="last:font-bold last:text-color-purple-600 text-color-blue-600 hover:underline">Contact (styled)</a>
                </nav>
            </div>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">not-first:</code>
              utilities to style all elements except the first child:
        </p>

        <div class="border border-color-gray-200 rounded p-4">
            <div class="space-y-0">
                <div class="not-first:border-t not-first:border-color-gray-300 p-3">First item (no top border)</div>
                <div class="not-first:border-t not-first:border-color-gray-300 p-3">Second item (has top border)</div>
                <div class="not-first:border-t not-first:border-color-gray-300 p-3">Third item (has top border)</div>
                <div class="not-first:border-t not-first:border-color-gray-300 p-3">Fourth item (has top border)</div>
            </div>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">not-last:</code>
              utilities to style all elements except the last child:
        </p>

        <div class="border border-color-gray-200 rounded p-4">
            <div class="space-y-0">
                <div class="not-last:border-b not-last:border-color-gray-300 p-3">First item (has bottom border)</div>
                <div class="not-last:border-b not-last:border-color-gray-300 p-3">Second item (has bottom border)</div>
                <div class="not-last:border-b not-last:border-color-gray-300 p-3">Third item (has bottom border)</div>
                <div class="not-last:border-b not-last:border-color-gray-300 p-3">Last item (no bottom border)</div>
            </div>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">odd:</code>
              utilities to style odd-numbered child elements:
        </p>

        <div class="space-y-4">
            <div class="border border-color-gray-200 rounded p-4">
                <div class="space-y-1">
                    <div class="odd:bg-color-blue-100 odd:text-color-blue-800 p-3 rounded">Item 1 (odd - styled)</div>
                    <div class="odd:bg-color-blue-100 odd:text-color-blue-800 p-3 rounded">Item 2 (even)</div>
                    <div class="odd:bg-color-blue-100 odd:text-color-blue-800 p-3 rounded">Item 3 (odd - styled)</div>
                    <div class="odd:bg-color-blue-100 odd:text-color-blue-800 p-3 rounded">Item 4 (even)</div>
                    <div class="odd:bg-color-blue-100 odd:text-color-blue-800 p-3 rounded">Item 5 (odd - styled)</div>
                </div>
            </div>
            
            <table class="w-full border border-color-gray-200 rounded">
                <tbody>
                    <tr class="odd:bg-color-gray-50">
                        <td class="p-3 border-b border-color-gray-200">Row 1</td>
                        <td class="p-3 border-b border-color-gray-200">Odd row styling</td>
                    </tr>
                    <tr class="odd:bg-color-gray-50">
                        <td class="p-3 border-b border-color-gray-200">Row 2</td>
                        <td class="p-3 border-b border-color-gray-200">Even row</td>
                    </tr>
                    <tr class="odd:bg-color-gray-50">
                        <td class="p-3 border-b border-color-gray-200">Row 3</td>
                        <td class="p-3 border-b border-color-gray-200">Odd row styling</td>
                    </tr>
                </tbody>
            </table>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">even:</code>
              utilities to style even-numbered child elements:
        </p>

        <div class="space-y-4">
            <div class="border border-color-gray-200 rounded p-4">
                <div class="space-y-1">
                    <div class="even:bg-color-green-100 even:text-color-green-800 p-3 rounded">Item 1 (odd)</div>
                    <div class="even:bg-color-green-100 even:text-color-green-800 p-3 rounded">Item 2 (even - styled)</div>
                    <div class="even:bg-color-green-100 even:text-color-green-800 p-3 rounded">Item 3 (odd)</div>
                    <div class="even:bg-color-green-100 even:text-color-green-800 p-3 rounded">Item 4 (even - styled)</div>
                    <div class="even:bg-color-green-100 even:text-color-green-800 p-3 rounded">Item 5 (odd)</div>
                </div>
            </div>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">placeholder:</code>
              utilities to style input placeholder text:
        </p>

        <div class="space-y-4">
            <input class="w-full placeholder:text-color-gray-400 placeholder:italic border-2 border-color-gray-300 px-3 py-2 rounded" placeholder="Italic gray placeholder">
            
            <input class="w-full placeholder:text-color-blue-500 placeholder:font-semibold border-2 border-color-gray-300 px-3 py-2 rounded" placeholder="Bold blue placeholder">
            
            <textarea class="w-full placeholder:text-color-purple-400 placeholder:text-sm border-2 border-color-gray-300 px-3 py-2 rounded" placeholder="Small purple placeholder text" rows="3"></textarea>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">file:</code>
              utilities to style file input buttons:
        </p>

        <div class="space-y-4">
            <input type="file" class="file:bg-color-blue-500 file:text-color-white file:border-0 file:px-4 file:py-2 file:rounded file:mr-4 file:cursor-pointer">
            
            <input type="file" class="file:bg-color-green-500 file:text-color-white file:border-0 file:px-6 file:py-3 file:rounded-lg file:mr-4 file:hover:bg-color-green-600 file:transition-colors">
            
            <input type="file" class="file:bg-color-purple-500 file:text-color-white file:border-2 file:border-color-purple-600 file:px-4 file:py-2 file:rounded-full file:mr-4">
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">marker:</code>
              utilities to style list item markers:
        </p>

        <div class="space-y-4">
            <ul class="list-disc pl-6 space-y-2">
                <li class="marker:text-color-blue-500">Blue bullet point</li>
                <li class="marker:text-color-red-500">Red bullet point</li>
                <li class="marker:text-color-green-500">Green bullet point</li>
            </ul>
            
            <ol class="list-decimal pl-6 space-y-2">
                <li class="marker:text-color-purple-500 marker:font-bold">Purple bold number</li>
                <li class="marker:text-color-orange-500 marker:font-bold">Orange bold number</li>
                <li class="marker:text-color-pink-500 marker:font-bold">Pink bold number</li>
            </ol>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">selection:</code>
              utilities to style selected text:
        </p>

        <div class="space-y-4">
            <p class="selection:bg-color-yellow-200 selection:text-color-black p-4 border border-color-gray-200 rounded">
                Select this text to see yellow highlighting with black text.
            </p>
            
            <p class="selection:bg-color-blue-500 selection:text-color-white p-4 border border-color-gray-200 rounded">
                Select this text to see blue highlighting with white text.
            </p>
            
            <p class="selection:bg-color-green-200 selection:text-color-green-800 p-4 border border-color-gray-200 rounded">
                Select this text to see green highlighting with dark green text.
            </p>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">first-line:</code>
              utilities to style the first line of text:
        </p>

        <div class="space-y-4">
            <p class="first-line:font-bold first-line:text-color-blue-600 p-4 border border-color-gray-200 rounded leading-relaxed">
                This is the first line of text that will be styled with bold font and blue color.
                This is the second line that will have normal styling and continue with the regular text formatting.
            </p>
            
            <p class="first-line:uppercase first-line:tracking-wide first-line:text-color-purple-600 p-4 border border-color-gray-200 rounded leading-relaxed">
                This first line will be uppercase with wide letter spacing and purple color.
                The rest of the paragraph will continue with normal case and spacing.
            </p>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">first-letter:</code>
              utilities to style the first letter of text:
        </p>

        <div class="space-y-4">
            <p class="first-letter:text-4xl first-letter:font-bold first-letter:text-color-red-500 first-letter:float-left first-letter:mr-2 first-letter:mt-1 p-4 border border-color-gray-200 rounded leading-relaxed">
                This paragraph demonstrates a drop cap effect where the first letter is much larger and styled differently from the rest of the text.
            </p>
            
            <p class="first-letter:text-3xl first-letter:font-bold first-letter:text-color-blue-600 first-letter:bg-color-blue-100 first-letter:px-2 first-letter:py-1 first-letter:rounded first-letter:mr-2 first-letter:float-left p-4 border border-color-gray-200 rounded leading-relaxed">
                Another example with a background color and padding on the first letter to create a more prominent drop cap effect.
            </p>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">backdrop:</code>
              utilities to style modal and dialog backdrops:
        </p>

        <div class="space-y-4">
            <button onclick="document.getElementById('modal1').showModal()" class="bg-color-blue-500 text-color-white px-4 py-2 rounded hover:bg-color-blue-600">
                Open Modal with Custom Backdrop
            </button>
            
            <dialog id="modal1" class="backdrop:bg-color-black backdrop:opacity-75 p-0 rounded-lg">
                <div class="bg-color-white p-6 rounded-lg">
                    <h3 class="text-lg font-semibold mb-4">Modal with Custom Backdrop</h3>
                    <p class="mb-4">This modal has a custom backdrop with 75% opacity.</p>
                    <button onclick="document.getElementById('modal1').close()" class="bg-color-gray-500 text-color-white px-4 py-2 rounded">Close</button>
                </div>
            </dialog>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">has-checked:</code>
              utilities to style elements that contain checked inputs:
        </p>

        <div class="space-y-4">
            <label class="has-checked:bg-color-green-100 has-checked:border-color-green-500 has-checked:text-color-green-800 border-2 border-color-gray-300 p-4 rounded flex items-center space-x-3 cursor-pointer transition-all">
                <input type="checkbox" class="w-5 h-5">
                <span class="font-medium">Check me to change parent styling</span>
            </label>
            
            <div class="has-checked:shadow-lg has-checked:scale-105 border border-color-gray-200 p-4 rounded transition-all">
                <label class="flex items-center space-x-3 cursor-pointer">
                    <input type="checkbox" class="w-5 h-5">
                    <span>Checking this will add shadow and scale to the container</span>
                </label>
            </div>
            
            <fieldset class="has-checked:bg-color-blue-50 has-checked:border-color-blue-300 border-2 border-color-gray-300 p-4 rounded transition-colors">
                <legend class="px-2 font-medium">Options</legend>
                <div class="space-y-2">
                    <label class="flex items-center space-x-2">
                        <input type="radio" name="options" class="w-4 h-4">
                        <span>Option 1</span>
                    </label>
                    <label class="flex items-center space-x-2">
                        <input type="radio" name="options" class="w-4 h-4">
                        <span>Option 2</span>
                    </label>
                </div>
            </fieldset>
        </div>
      `
    },
    // {
    //   data: `
    //     <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
    //         Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">indeterminate:</code>
    //           utilities to style indeterminate checkboxes:
    //     </p>

    //     <div class="space-y-4">
    //         <label class="flex items-center space-x-3">
    //             <input type="checkbox" id="indeterminate1" class="indeterminate:bg-color-blue-500 indeterminate:border-color-blue-500 w-5 h-5">
    //             <span>Indeterminate checkbox (blue)</span>
    //         </label>
            
    //         <label class="flex items-center space-x-3">
    //             <input type="checkbox" id="indeterminate2" class="indeterminate:bg-color-purple-500 indeterminate:border-color-purple-500 indeterminate:scale-110 w-5 h-5 transition-transform">
    //             <span>Indeterminate with scale effect</span>
    //         </label>
            
    //         <script>
    //             document.getElementById('indeterminate1').indeterminate = true;
    //             document.getElementById('indeterminate2').indeterminate = true;
    //         </script>
    //     </div>
    //   `
    // },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">peer-*:</code>
              utilities to style elements based on sibling states:
        </p>

        <div class="space-y-6">
            <div class="space-y-2">
                <input class="peer w-full border-2 border-color-gray-300 px-3 py-2 rounded focus:border-color-blue-500 focus:outline-none" placeholder="Focus me">
                <p class="peer-focus:text-color-blue-600 peer-focus:font-semibold text-color-gray-500 transition-all">This text changes when the input above is focused</p>
            </div>
            
            <div class="space-y-2">
                <input type="checkbox" class="peer w-5 h-5">
                <div class="peer-checked:bg-color-green-100 peer-checked:border-color-green-500 peer-checked:text-color-green-800 border-2 border-color-gray-300 p-4 rounded transition-all">
                    <p class="font-medium">This container changes when checkbox is checked</p>
                    <p class="text-sm opacity-75">Background, border, and text color all change</p>
                </div>
            </div>
            
            <div class="flex items-center space-x-4">
                <button class="peer bg-color-red-500 hover:bg-color-red-600 text-color-white px-4 py-2 rounded">Hover me</button>
                <span class="peer-hover:text-color-red-600 peer-hover:font-bold transition-all">This text reacts to button hover</span>
            </div>
        </div>
      `
    },
    {
      data: `
        <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
            Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">not-*:</code>
              utilities to style elements when they're NOT in certain states:
        </p>

        <div class="space-y-4">
            <button class="not-hover:opacity-75 not-disabled:cursor-pointer bg-color-blue-500 text-color-white px-4 py-2 rounded transition-opacity">
                Hover to see full opacity
            </button>
            
            <div class="flex gap-4">
                <button class="not-focus:shadow-none shadow-lg bg-color-green-500 text-color-white px-4 py-2 rounded transition-shadow">
                    Focus to remove shadow
                </button>
                
                <button disabled class="not-disabled:bg-color-purple-500 not-disabled:hover:bg-color-purple-600 bg-color-gray-400 text-color-white px-4 py-2 rounded cursor-not-allowed">
                    Disabled button
                </button>
            </div>
            
            <input type="checkbox" class="not-checked:opacity-50 w-5 h-5 transition-opacity">
            <span class="ml-2">Checkbox becomes opaque when checked</span>
        </div>
      `
    }
  ]
};

export default hoverFocusAndOtherStates;

<div className="
  text-color-gray-600 text-sm dark:text-color-gray-300 mb-10
  text-color-gray-800 dark:text-color-white font-semibold
  space-y-4 flex flex-wrap gap-4 items-center space-x-2 block
  w-full border-2 border-color-gray-300 px-3 py-2 rounded transition-colors transition-all
  bg-color-blue-500 hover:bg-color-blue-600 text-color-white px-4 py-2 rounded transition-colors
  bg-color-gray-200 hover:bg-color-blue-100 hover:scale-105 hover:shadow-lg duration-300 p-4 cursor-pointer
  text-color-gray-600 hover:text-color-blue-600 hover:underline cursor-pointer
  focus:border-color-blue-500 focus:outline-none focus:ring-2 focus:ring-color-blue-200
  focus:border-color-purple-500 focus:bg-color-purple-50 focus:shadow-lg
  bg-color-green-500 active:bg-color-green-700 active:scale-95
  bg-color-red-500 active:bg-color-red-700 active:shadow-inner shadow-lg
  bg-color-purple-500 active:bg-color-purple-700
  text-color-blue-600 visited:text-color-purple-600 underline
  text-color-green-600 visited:text-color-orange-600 hover:text-color-green-700 visited:hover:text-color-orange-700
  bg-color-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-color-gray-500
  disabled:bg-color-gray-100 disabled:text-color-gray-400
  w-4 h-4 checked:bg-color-green-500 checked:border-color-green-500 checked:scale-110 transition-transform
  checked:bg-color-green-500 checked:border-color-green-500
  checked:bg-color-purple-500 checked:border-color-purple-500
  focus-visible:ring-2 focus-visible:ring-color-blue-300 focus-visible:ring-4 focus-visible:ring-color-green-200 focus-visible:ring-offset-2
  focus-visible:border-color-purple-500 focus-visible:ring-color-purple-200
  invalid:border-color-red-500 invalid:text-color-red-600 invalid:bg-color-red-50 invalid:shadow-red-200 invalid:shadow-lg
  required:border-color-orange-400 required:border-l-4 required:bg-color-orange-50
  read-only:bg-color-gray-100 read-only:text-color-gray-600 read-only:border-color-blue-300 read-only:bg-color-blue-50
  indeterminate:bg-color-blue-500 indeterminate:border-color-blue-500 indeterminate:bg-color-purple-500 indeterminate:border-color-purple-500 indeterminate:scale-110
  valid:border-color-green-500 valid:bg-color-green-50 valid:shadow-green-200 valid:shadow-md
  in-range:border-color-green-500 in-range:bg-color-green-50 in-range:accent-color-green-500
  out-of-range:border-color-red-500 out-of-range:bg-color-red-50 out-of-range:accent-color-red-500
  focus-within:border-color-blue-500 focus-within:border-color-purple-500 focus-within:bg-color-purple-50 focus-within:shadow-lg focus-within:border-color-green-500 focus-within:scale-105 transition-transform
  outline-none resize-none border border-color-gray-200 font-medium mb-2
  hover:text-color-blue-700 target:bg-color-yellow-200 target:border-l-4 target:border-color-yellow-500 target:bg-color-blue-200 target:shadow-lg target:scale-105 target:bg-color-green-200 target:ring-2 target:ring-color-green-500
  before:content-[→] before:mr-2 before:text-color-blue-500 before:content-[⭐] before:text-color-yellow-500 before:text-lg
  before:content-[] before:w-4 before:h-4 before:bg-color-red-500 before:rounded-full before:inline-block
  before:content-[] before:text-4xl before:text-color-gray-400 before:leading-none
  after:content-[←] after:ml-2 after:text-color-red-500 after:content-[✓] after:text-color-green-500 after:font-bold
  after:content-[] after:w-4 after:h-4 after:bg-color-purple-500 after:rounded-full after:inline-block
  after:content-[_↗] after:text-sm after:text-color-blue-400 hover:after:text-color-blue-600
  after:content-[] after:text-4xl after:text-color-gray-400 after:leading-none after:ml-2
  font-bold italic text-color-gray-600 text-sm dark:text-color-gray-300 mb-10 text-color-gray-800 dark:text-color-white font-semibold space-y-4 flex flex-wrap gap-4 items-center space-x-2 block w-full border-2 border-color-gray-300 px-3 py-2
  rounded transition-colors transition-all bg-color-blue-500 hover:bg-color-blue-600 text-color-white
  px-4 py-2 rounded transition-colors bg-color-gray-200 hover:bg-color-blue-100 hover:scale-105
  hover:shadow-lg duration-300 p-4 cursor-pointer text-color-gray-600 hover:text-color-blue-600
  hover:underline cursor-pointer focus:border-color-blue-500 focus:outline-none focus:ring-2
  focus:ring-color-blue-200 focus:border-color-purple-500 focus:bg-color-purple-50 focus:shadow-lg
  bg-color-green-500 active:bg-color-green-700 active:scale-95 bg-color-red-500 active:bg-color-red-700
  active:shadow-inner shadow-lg bg-color-purple-500 active:bg-color-purple-700 text-color-blue-600
  visited:text-color-purple-600 underline text-color-green-600 visited:text-color-orange-600
  hover:text-color-green-700 visited:hover:text-color-orange-700 bg-color-gray-300 disabled:opacity-50
  disabled:cursor-not-allowed text-color-gray-500 disabled:bg-color-gray-100 disabled:text-color-gray-400 w-4 h-4
  checked:bg-color-green-500 checked:border-color-green-500 checked:scale-110 transition-transform checked:bg-color-blue-500
  checked:border-color-blue-500 checked:bg-color-purple-500 checked:border-color-purple-500 focus-visible:ring-2
  focus-visible:ring-color-blue-300 focus-visible:ring-4 focus-visible:ring-color-green-200 focus-visible:ring-offset-2
  focus-visible:border-color-purple-500 focus-visible:ring-color-purple-200 invalid:border-color-red-500 invalid:text-color-red-600
  invalid:bg-color-red-50 invalid:shadow-red-200 invalid:shadow-lg required:border-color-orange-400 required:border-l-4
  required:bg-color-orange-50 read-only:bg-color-gray-100 read-only:text-color-gray-600 read-only:border-color-blue-300
  read-only:bg-color-blue-50 indeterminate:bg-color-blue-500 indeterminate:border-color-blue-500 indeterminate:bg-color-purple-500
  indeterminate:border-color-purple-500 indeterminate:scale-110 valid:border-color-green-500 valid:bg-color-green-50
  valid:shadow-green-200 valid:shadow-md in-range:border-color-green-500 in-range:bg-color-green-50 in-range:accent-color-green-500
  out-of-range:border-color-red-500 out-of-range:bg-color-red-50 out-of-range:accent-color-red-500 focus-within:border-color-blue-500
  focus-within:border-color-purple-500 focus-within:bg-color-purple-50 focus-within:shadow-lg focus-within:border-color-green-500
  focus-within:scale-105 transition-transform outline-none resize-none border border-color-gray-200 font-medium mb-2
  hover:text-color-blue-700 target:bg-color-yellow-200 target:border-l-4 target:border-color-yellow-500 target:bg-color-blue-200 target:shadow-lg target:scale-105 target:bg-color-green-200 target:ring-2 target:ring-color-green-500 before:content-[→] before:mr-2
  before:text-color-blue-500 before:content-[⭐] before:text-color-yellow-500 before:text-lg before:content-[] before:w-4 before:h-4 before:bg-color-red-500 before:rounded-full before:inline-block before:content-[] before:text-4xl before:text-color-gray-400 before:leading-none after:content-[←] after:ml-2 after:text-color-red-500 after:content-[✓] after:text-color-green-500 after:font-bold after:content-[] after:w-4 after:h-4 after:bg-color-purple-500 after:rounded-full after:inline-block after:content-[_↗] after:text-sm after:text-color-blue-400 hover:after:text-color-blue-600
  after:content-[] after:text-4xl after:text-color-gray-400 after:leading-none after:ml-2 font-bold italic first:bg-color-blue-500 first:text-color-white first:font-bold first:text-color-green-600 last:bg-color-red-500 last:text-color-white last:font-bold last:text-color-purple-600 not-first:border-t not-first:border-color-gray-300 not-last:border-b not-last:border-color-gray-300 odd:bg-color-gray-100 odd:bg-color-blue-100 odd:text-color-blue-800 odd:bg-color-gray-50 even:bg-color-blue-50 even:bg-color-green-100 even:text-color-green-800 placeholder:text-color-gray-400 placeholder:italic
  placeholder:text-color-blue-500 placeholder:font-semibold placeholder:text-color-purple-400 placeholder:text-sm file:bg-color-blue-500 file:text-color-white file:border-0 file:px-4 file:py-2 file:rounded file:mr-4 file:cursor-pointer file:bg-color-green-500 file:px-6 file:py-3 file:rounded-lg file:hover:bg-color-green-600 file:transition-colors file:bg-color-purple-500 file:border-2 file:border-color-purple-600 file:rounded-full marker:text-color-blue-500 marker:text-color-red-500 marker:text-color-green-500 marker:text-color-purple-500 marker:font-bold marker:text-color-orange-500 marker:text-color-pink-500 selection:bg-color-yellow-200 selection:text-color-black selection:bg-color-blue-500 selection:text-color-white selection:bg-color-green-200 selection:text-color-green-800 first-line:font-bold first-line:text-color-blue-600 first-line:uppercase first-line:tracking-wide first-line:text-color-purple-600 first-letter:text-4xl first-letter:font-bold first-letter:text-color-red-500 first-letter:float-left first-letter:mr-2 first-letter:mt-1 first-letter:text-3xl first-letter:text-color-blue-600 first-letter:bg-color-blue-100 first-letter:px-2 first-letter:py-1 first-letter:rounded backdrop:bg-color-black backdrop:opacity-75 has-checked:bg-color-green-100 has-checked:border-color-green-500 has-checked:text-color-green-800 has-checked:shadow-lg has-checked:scale-105 has-checked:bg-color-blue-50 has-checked:border-color-blue-300 peer peer-focus:text-color-blue-600 peer-focus:font-semibold peer-checked:bg-color-green-100 peer-checked:border-color-green-500 peer-checked:text-color-green-800 peer-hover:text-color-red-600 peer-hover:font-bold not-hover:opacity-75 not-disabled:cursor-pointer not-focus:shadow-none not-disabled:bg-color-purple-500 not-disabled:hover:bg-color-purple-600 not-checked:opacity-50 space-y-0 space-y-1 space-y-2 space-y-6 list-disc pl-6 list-decimal leading-relaxed uppercase tracking-wide float-left mr-2 mt-1 px-2 py-1 bg-color-white rounded-lg showModal close w-5 h-5 cursor-pointer font-medium text-lg mb-4 bg-color-gray-500
"></div>